import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Chip, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, Button, Tooltip, useTheme, Collapse,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonIcon from "@mui/icons-material/Person";
import { getUsersActivity, getUserActivity } from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";

/* ── status vocabulary (semantic, not the teal accent) ── */
const STATUS = {
  active:  { label: "Active",  color: "#1f9e83" },
  new:     { label: "New",     color: "#3b7fd1" },
  idle:    { label: "Idle",    color: "#c79218" },
  stalled: { label: "Stalled", color: "#d9713a" },
  errored: { label: "Errored", color: "#cf4b45" },
  dormant: { label: "Dormant", color: "#9aa2ab" },
};
const STAGES = ["Onboarded", "Loaded data", "Analyzed", "Modeled", "Activated"];
const ATTENTION = new Set(["stalled", "errored"]);

function relTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 90) return "just now";
  const m = s / 60; if (m < 60) return `${Math.round(m)}m ago`;
  const h = m / 60; if (h < 24) return `${Math.round(h)}h ago`;
  const d = h / 24; if (d < 30) return `${Math.round(d)}d ago`;
  const mo = d / 30; if (mo < 12) return `${Math.round(mo)}mo ago`;
  return `${Math.round(d / 365)}y ago`;
}

/* ── inline sparkline ── */
function Sparkline({ values = [], color, height = 22 }) {
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const w = 100, pad = 2;
  const pts = values.map((v, i) => {
    const x = values.length > 1 ? (i / (values.length - 1)) * w : w;
    const y = pad + (1 - v / max) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const [ex, ey] = pts[pts.length - 1].split(",");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none"
         style={{ width: 96, height, display: "block" }}>
      <polyline points={pts.join(" ")} fill="none" stroke={color}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={ex} cy={ey} r="2.3" fill={color} />
    </svg>
  );
}

/* ── funnel meter ── */
function Funnel({ stageIndex = 0, stuck, theme }) {
  const accent = theme.palette.primary.main;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex", gap: "3px" }}>
        {STAGES.map((_, i) => {
          const on = i <= stageIndex;
          const isStuck = stuck && i === stageIndex;
          return (
            <Box key={i} sx={{
              width: 15, height: 6, borderRadius: "2px",
              bgcolor: isStuck ? STATUS.stalled.color : on ? accent : theme.palette.divider,
            }} />
          );
        })}
      </Box>
      <Typography variant="caption" sx={{ color: stuck ? STATUS.stalled.color : "text.secondary", whiteSpace: "nowrap" }}>
        {STAGES[stageIndex]}
      </Typography>
    </Box>
  );
}

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.dormant;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      fontSize: 12, fontWeight: 600, px: 1.1, py: 0.4, borderRadius: 999,
      color: s.color, bgcolor: `${s.color}22`, whiteSpace: "nowrap",
    }}>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: s.color }} />
      {s.label}
    </Box>
  );
}

/* ── drill-down timeline (lazy-loaded per user) ── */
function UserTimeline({ userId }) {
  const theme = useTheme();
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getUserActivity(userId)
      .then((d) => { if (!cancelled) setEvents(d.events || []); })
      .catch((e) => { if (!cancelled) setErr(e.message || "Failed to load"); });
    return () => { cancelled = true; };
  }, [userId]);

  const dotColor = (ev) => {
    if (ev.outcome === "failed") return STATUS.errored.color;
    if (ev.outcome === "ok") return STATUS.active.color;
    if (ev.type === "upload") return STATUS.new.color;
    if (ev.type === "deploy") return STATUS.active.color;
    return theme.palette.primary.main;
  };

  if (err) return <Typography variant="caption" color="error" sx={{ p: 2, display: "block" }}>{err}</Typography>;
  if (events === null) return <Box sx={{ p: 2 }}><CircularProgress size={18} /></Box>;
  if (!events.length) return <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: "block" }}>No recorded activity yet.</Typography>;

  return (
    <Box sx={{ p: "16px 18px 20px 30px" }}>
      <Typography variant="caption" sx={{ letterSpacing: ".1em", textTransform: "uppercase", color: "text.secondary", fontWeight: 700, display: "block", mb: 1.5 }}>
        Activity timeline
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, position: "relative",
        "&::before": { content: '""', position: "absolute", left: "6px", top: 4, bottom: 4, width: "2px", bgcolor: "divider" } }}>
        {events.map((ev, i) => (
          <Box component="li" key={i} sx={{ position: "relative", pl: 3, pb: i === events.length - 1 ? 0 : 1.75 }}>
            <Box sx={{ position: "absolute", left: 0, top: "3px", width: 14, height: 14, borderRadius: "50%",
              border: `2px solid ${theme.palette.background.paper}`, bgcolor: dotColor(ev) }} />
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {ev.label}
              {ev.meta?.row_count != null && (
                <Typography component="span" variant="caption" color="text.secondary"> · {Number(ev.meta.row_count).toLocaleString()} rows</Typography>
              )}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
              {new Date(ev.ts).toLocaleDateString()} · {relTime(ev.ts)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Tile({ label, value, sub, accent }) {
  const theme = useTheme();
  return (
    <Box sx={{ flex: "1 1 150px", minWidth: 130, bgcolor: "background.paper",
      border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: "13px 14px" }}>
      <Typography variant="caption" sx={{ letterSpacing: ".07em", textTransform: "uppercase", color: "text.secondary", fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: "-.02em", mt: 0.7, fontVariantNumeric: "tabular-nums", color: "text.primary" }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: accent || "text.disabled", mt: 0.3, display: "block" }}>{sub}</Typography>}
    </Box>
  );
}

/* Standalone admin shell — this page lives OUTSIDE the analytics app (no
   product sidebar), so it carries its own minimal chrome. */
function Shell({ children }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 3 }, py: 1.2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 700, letterSpacing: "-.01em" }}>Assertion</Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", letterSpacing: ".1em", textTransform: "uppercase" }}>Admin</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" href="/" sx={{ textTransform: "none" }}>← Studio</Button>
        </Box>
      </Box>
      {children}
    </Box>
  );
}

export default function UsageMonitor() {
  const theme = useTheme();
  const { isAdmin, setActingOrg, setActingUser } = useImpersonation();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [orgFilter, setOrgFilter] = useState(null);
  const [attnOnly, setAttnOnly] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getUsersActivity()
      .then((d) => { if (!cancelled) setRows(d.users || []); })
      .catch((e) => { if (!cancelled) setError(e.message || "Failed to load"); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const orgs = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => { if (r.org_id && !m.has(r.org_id)) m.set(r.org_id, r.domain || r.org_id); });
    return [...m.entries()].map(([id, domain]) => ({ id, domain })).sort((a, b) => a.domain.localeCompare(b.domain));
  }, [rows]);

  const shown = useMemo(() => {
    let r = rows || [];
    if (orgFilter) r = r.filter((x) => x.org_id === orgFilter);
    if (attnOnly) r = r.filter((x) => ATTENTION.has(x.status));
    return r;
  }, [rows, orgFilter, attnOnly]);

  const fleet = useMemo(() => {
    const r = rows || [];
    const active7 = r.filter((x) => x.days_since_active != null && x.days_since_active <= 7).length;
    const projects = r.reduce((a, x) => a + (x.counts?.projects || 0), 0);
    const runsOk = r.reduce((a, x) => a + (x.counts?.runs_ok || 0), 0);
    const runsAll = r.reduce((a, x) => a + (x.counts?.runs_ok || 0) + (x.counts?.runs_failed || 0), 0);
    const reports = r.reduce((a, x) => a + (x.outcomes?.reports || 0), 0);
    const deployed = r.reduce((a, x) => a + (x.outcomes?.deployed || 0), 0);
    const attn = r.filter((x) => ATTENTION.has(x.status)).length;
    return { total: r.length, active7, projects, runsOk, runsAll, reports, deployed, attn };
  }, [rows]);

  const viewAs = (r) => {
    if (!r.org_id) return;
    setActingOrg(r.org_id);
    setActingUser({ user_id: r.user_id, email: r.email, display_name: r.display_name, is_test: r.is_test });
    window.location.href = "/analyze";
  };

  if (!isAdmin) {
    return <Shell><Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>Admins only.</Box></Shell>;
  }

  const line = theme.palette.divider;
  const th = { fontFamily: "monospace", fontSize: 10.5, letterSpacing: ".07em", textTransform: "uppercase", color: "text.secondary", fontWeight: 600, borderBottom: `1px solid ${line}`, py: 1.4, whiteSpace: "nowrap" };
  const td = { borderBottom: `1px solid ${theme.palette.action.hover}`, py: 1.6 };

  return (
    <Shell>
    <Box sx={{ maxWidth: 1180, mx: "auto", p: { xs: 2, sm: 3 } }}>
      <Typography variant="caption" sx={{ color: "primary.main", fontFamily: "monospace", letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 600 }}>ML Analytics · Admin</Typography>
      <Typography variant="h5" sx={{ fontWeight: 680, letterSpacing: "-.02em", mt: 0.5, mb: 0.5 }}>Usage Monitor</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "66ch", mb: 3 }}>
        How each user is using the product — who's active, what they've built, and who's stuck. Click a row to see their activity.
      </Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {rows === null && !error && <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={28} /></Box>}

      {rows !== null && (
        <>
          {/* fleet tiles */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
            <Tile label="Active users" value={`${fleet.active7} / ${fleet.total}`} sub="active in 7d" />
            <Tile label="Projects" value={fleet.projects} sub="total" />
            <Tile label="Models trained" value={fleet.runsOk} sub={fleet.runsAll ? `${Math.round((fleet.runsOk / fleet.runsAll) * 100)}% succeeded` : "—"} accent={STATUS.active.color} />
            <Tile label="Reports" value={fleet.reports} sub="generated" />
            <Tile label="Deployed models" value={fleet.deployed} sub="live" />
            <Tile label="Needs attention" value={fleet.attn} sub="stalled or errored" accent={fleet.attn ? STATUS.stalled.color : undefined} />
          </Box>

          {/* table card */}
          <Box sx={{ bgcolor: "background.paper", border: `1px solid ${line}`, borderRadius: 3, overflow: "hidden" }}>
            {/* toolbar */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", p: 1.5, borderBottom: `1px solid ${theme.palette.action.hover}` }}>
              <Chip label="All orgs" size="small" onClick={() => setOrgFilter(null)}
                variant={orgFilter ? "outlined" : "filled"}
                color={orgFilter ? "default" : "primary"} />
              {orgs.map((o) => (
                <Chip key={o.id} label={o.domain} size="small" onClick={() => setOrgFilter(o.id)}
                  variant={orgFilter === o.id ? "filled" : "outlined"}
                  color={orgFilter === o.id ? "primary" : "default"} />
              ))}
              <Box sx={{ flex: 1 }} />
              <Chip label={`⚠ Needs attention · ${fleet.attn}`} size="small"
                onClick={() => setAttnOnly((v) => !v)}
                variant={attnOnly ? "filled" : "outlined"}
                sx={attnOnly ? { bgcolor: `${STATUS.stalled.color}22`, color: STATUS.stalled.color } : {}} />
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={th}>User</TableCell>
                    <TableCell sx={th}>Last active ↓</TableCell>
                    <TableCell sx={th}>Status</TableCell>
                    <TableCell sx={th}>Furthest stage</TableCell>
                    <TableCell sx={th}>Activity · 8 wks</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>Proj</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>Sessions</TableCell>
                    <TableCell sx={{ ...th, textAlign: "right" }}>Runs</TableCell>
                    <TableCell sx={th}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => {
                    const open = expanded === r.user_id;
                    const sColor = (STATUS[r.status] || STATUS.dormant).color;
                    const c = r.counts || {};
                    const late = r.days_since_active != null && r.days_since_active > 21;
                    const warn = r.days_since_active != null && r.days_since_active > 7 && r.days_since_active <= 21;
                    return (
                      <Fragment key={r.user_id}>
                        <TableRow hover sx={{ cursor: "pointer" }}
                          onClick={() => setExpanded(open ? null : r.user_id)}>
                          <TableCell sx={td}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {open ? <KeyboardArrowDownIcon fontSize="small" sx={{ opacity: .6 }} /> : <KeyboardArrowRightIcon fontSize="small" sx={{ opacity: .6 }} />}
                              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }} noWrap>{r.email || r.display_name || r.user_id}</Typography>
                                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }} noWrap>{r.domain || r.org_id}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...td, color: late ? STATUS.errored.color : warn ? STATUS.stalled.color : "text.primary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{relTime(r.last_active_at)}</TableCell>
                          <TableCell sx={td}><StatusPill status={r.status} /></TableCell>
                          <TableCell sx={td}><Funnel stageIndex={r.stage_index ?? 0} stuck={r.stuck} theme={theme} /></TableCell>
                          <TableCell sx={td}><Sparkline values={r.sparkline} color={sColor} /></TableCell>
                          <TableCell sx={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.projects ?? 0}</TableCell>
                          <TableCell sx={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.sessions ?? 0}</TableCell>
                          <TableCell sx={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            <Box component="span" sx={{ color: STATUS.active.color }}>{c.runs_ok ?? 0}</Box>
                            <Box component="span" sx={{ color: "text.disabled", px: 0.3 }}>/</Box>
                            <Box component="span" sx={{ color: (c.runs_failed ? STATUS.errored.color : "text.disabled") }}>{c.runs_failed ?? 0}</Box>
                          </TableCell>
                          <TableCell sx={td} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={r.is_test ? "Test org · read-write" : "Read-only view"}>
                              <span>
                                <Button size="small" onClick={() => viewAs(r)} startIcon={<PersonIcon sx={{ fontSize: 15 }} />}
                                  sx={{ textTransform: "none", fontSize: 12 }}>
                                  View as
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0, borderBottom: open ? `1px solid ${line}` : "none", bgcolor: theme.palette.action.hover }}>
                            <Collapse in={open} unmountOnExit>
                              {open && <UserTimeline userId={r.user_id} />}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                  {shown.length === 0 && (
                    <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>No users match.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
            Funnel: Onboarded → Loaded data → Analyzed → Modeled → Activated (deployed a model or generated a report). Status thresholds: active ≤7d · idle ≤21d · stalled &gt;21d · dormant &gt;30d.
          </Typography>
        </>
      )}
    </Box>
    </Shell>
  );
}
